import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Loader2, FileText, Shield, Gift, AlertCircle, Info } from "lucide-react";

// OCI V2.24B — Charity-visible, READ-ONLY donor-facing campaign wording.
// Charity users can SEE (not edit) solicitation/disclosure text, one-off disclosure,
// Direct Debit guarantee/mandate wording and Gift Aid statement, per campaign.
function WordingBlock({ icon: Icon, tone, label, text, fallback }) {
  const toneCls = {
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  }[tone] || "bg-slate-50 border-slate-200 text-slate-700";
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className={`rounded-lg border p-3 text-sm ${text ? toneCls : "bg-slate-50 border-slate-200 text-slate-400 italic"}`}>
        {text || fallback}
      </div>
    </div>
  );
}

export default function CharityCampaignInfo() {
  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState([]);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await apiClient.get("/charity/campaign-info");
      setCampaigns(res.data?.campaigns || []);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load campaign info");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-500 text-sm" data-testid="charity-campaign-info-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading campaign info…
      </div>
    );
  }
  if (err) {
    return <Card className="border-rose-200 bg-rose-50"><CardContent className="p-4 text-sm text-rose-800" data-testid="charity-campaign-info-error">{err}</CardContent></Card>;
  }

  return (
    <div className="space-y-4" data-testid="charity-campaign-info">
      <div>
        <h2 className="font-heading font-bold text-lg text-slate-900">Campaign Info</h2>
        <p className="text-sm text-slate-500">The donor-facing wording shown on each of your campaigns. Read-only — your agency manages this text.</p>
      </div>

      {/* Donor communications visibility (read-only) */}
      <Card className="border-slate-200 bg-slate-50" data-testid="charity-comms-note">
        <CardContent className="p-4 flex items-start gap-2.5">
          <Info className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-slate-600">
            <span className="font-semibold text-slate-800">Donor communications</span> for your campaigns
            currently send via the platform sender. To use your own charity sender / Resend domain for
            donor thank-you and welcome emails, contact your platform administrator.
          </div>
        </CardContent>
      </Card>

      {campaigns.length === 0 ? (
        <Card className="border-slate-200"><CardContent className="p-6 text-center text-sm text-slate-500" data-testid="charity-campaign-info-empty">No campaigns linked to your charity yet.</CardContent></Card>
      ) : campaigns.map((c) => (
        <Card key={c.id} className="border-slate-200" data-testid={`charity-campaign-card-${c.id}`}>
          <CardContent className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">{c.name}</h3>
              <Badge className={c.giftAidEnabled ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}>
                Gift Aid {c.giftAidEnabled ? "on" : "off"}
              </Badge>
            </div>
            <WordingBlock icon={AlertCircle} tone="amber" label="Solicitation / Disclosure" text={c.disclosureText} fallback="Default GDPR wording (no custom text set)." />
            <WordingBlock icon={AlertCircle} tone="amber" label="One-off Donation Disclosure" text={c.oneOffDisclosureText} fallback="Not set." />
            <WordingBlock icon={Shield} tone="blue" label="Direct Debit Guarantee / Mandate Wording" text={c.directDebitGuaranteeText} fallback="Not set." />
            {c.giftAidEnabled && (
              <WordingBlock icon={Gift} tone="emerald" label="Gift Aid Statement" text={c.giftAidStatement} fallback="Default UK Gift Aid wording." />
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
